import { rawLocalStorage } from "@sfc/ui/local-storage";
import { emitBounded } from "@lib/socketTransport/send";
import {
  areRoundApplicationsOpen,
  getRoundApplicationAudience,
} from "@engine/round/accessors";
import { Capacitor } from "@capacitor/core";
import * as Sentry from "@client/telemetry/sentry";
import EventEmitter from "eventemitter3";
import { useCallback, useEffect, useRef } from "react";
import type { Socket as IoSocket } from "socket.io-client";
import { io } from "socket.io-client";

import { getPostHogSessionReplay } from "@client/telemetry/posthog";
import { getClientBaseUrl } from "@client/trpc";
// Impersonation state lives in its own leaf module (cycle break: trpc needs
// the ref, this file needs trpc's URL helpers). Re-exported so existing
// consumers keep importing from here.
import {
  getGlobalImpersonation,
  impersonationRef,
  notifyImpersonationListeners,
  setGlobalImpersonation,
  stampImpersonationStateOwner,
} from "@client/impersonationRef";
export {
  clearGlobalImpersonation,
  getGlobalImpersonation,
  impersonationRef,
  notifyImpersonationListeners,
  setGlobalImpersonation,
  subscribeImpersonationListener,
  useImpersonationRefValue,
  type GlobalImpersonation,
} from "@client/impersonationRef";
import { loadAuthToken } from "@client/iphone/lib/auth";
import { getIsOnline } from "@client/hooks/useNetworkStatus";
import { isIPad, isIPadCapacitor } from "@client/lib/isIPad";
import { indexedDBWasMissingAtBoot } from "@client/offline/hasIndexedDB";
import {
  connectErrorRetryDelayMs,
  isSessionRejection,
} from "@client/offline/socketConnectErrorPolicy";
import { runEmitWithRetry } from "@client/offline/socketEmitPolicy";
import {
  createServerMessageProcessor,
  summarizeUnparsedServerMessage,
} from "@client/serverMessagePolicy";

import { trackSchema, type AppMode, type Track } from "@lib/types";
import {
  type EditLock,
  EditLockedError,
  getEditLock,
} from "@client/lib/editLock";
import { toast } from "sonner";
export type { AppMode };
import { captureExceptionWithPriority } from "@lib/telemetry/sentry";
import userIsAdmin from "@client/utils/userIsAdmin";
import type { Client } from "@engine/Client";
import { holdRoundTabPresence } from "@engine/roundTabPresence";
import type { IncomingUnconfirmedActionMessage } from "@engine/core";
import {
  type ClientStorageAdapter,
  type MessageToClient,
  type MessageToServer,
  summarizeMessageToServer,
  summarizeMessageToClient,
} from "@engine/network";
import type {
  CurrentAction,
  StampableAction,
  ActionType,
} from "@engine/round/actions";
import type { RoundAggregate } from "@engine/round/aggregate";
import type ClientInfoStore from "@engine/round/infoStore/ClientInfoStore";
import SPAStorageAdapterWrapper from "@engine/storageAdapters/SPAStorageAdapterWrapper";
import type { UserProfiles } from "@lib/types";
import { anonymizeAggregate } from "@lib/utils/anonymization";
import { assertUnreachable } from "@lib/utils/assertUnreachable";
import { z } from "zod";

type RoundClient = Client;

export type RoundData = {
  client: RoundClient;
  userProfiles: UserProfiles;
  underlyingUserIsSfcAdmin: boolean;
  /** Whether the effective viewer is a speculator — see State.viewerIsSpeculator. */
  viewerIsSpeculator: boolean;
};

/**
 * Truncated record of a raw frame received from the engine.io transport, kept
 * in a small ring buffer so a "parse error" disconnect can report the bytes
 * socket.io-parser rejected. The parser throws a bare `Error("invalid payload")`
 * without the offending data, so this buffer is the only way to recover it.
 */
export type RawSocketFrame = {
  /** Epoch ms when the frame arrived. */
  at: number;
  /** "string", "ArrayBuffer", etc. Parse errors are always on string frames. */
  type: string;
  /** Full length before truncation (chars for strings, bytes otherwise). */
  length: number;
  /** Leading slice of the frame, capped to avoid bloating Sentry payloads. */
  head: string;
};

export type DisconnectDetails = {
  reason: IoSocket.DisconnectReason;
  description?: DisconnectDescription | undefined;
  willReconnect: boolean;
  roundId: string;
  clientId: string;
  /** Most recent raw frames, oldest first; the last is usually the culprit. */
  recentRawFrames: RawSocketFrame[];
};

export type SocketStatus = "connected" | DisconnectDetails | null;

/**
 * Why the app is deliberately closing its own connection for good. Reported
 * to the server (volatile, best-effort) right before the socket closes so
 * the connection alerts can announce these immediately and by name — they
 * never un-happen, so there is nothing to wait for.
 */
export type TerminalDisconnectCause = "stale-build" | "schema-skew";

export type State = {
  underlyingUserId: string;
  /** Raw underlying admin flag — use effectiveIsSfcAdmin instead for permission checks. */
  underlyingUserIsSfcAdmin: boolean;
  /** Impersonation-aware: false when impersonating, otherwise matches underlyingUserIsSfcAdmin. Use this for permission checks. */
  effectiveIsSfcAdmin: boolean;
  actingAsUserId: string;
  round: RoundAggregate;
  /** Confirmed manual availability; subscribers evaluate the time window live. */
  confirmedApplicationsOpen: boolean;
  confirmedApplicationWindow: RoundAggregate["applicationWindow"];
  /** Server-confirmed audience for application access reporting. */
  confirmedApplicationAudience: ReturnType<typeof getRoundApplicationAudience>;
  userProfiles: UserProfiles;
  mode: AppMode;
  unconfirmedActions: Promise<
    {
      createdAt: number;
      message: IncomingUnconfirmedActionMessage;
    }[]
  >;
  subscribe(eventType: ActionType, cb: (action: CurrentAction) => void): void;
  unsubscribe(
    eventType: ActionType,
    cb: (eventDetail: CurrentAction) => void,
  ): void;
  socketStatus: SocketStatus;
  /**
   * True once a server message failed schema validation — this build is
   * older than the deployed action schema and has been disconnected for
   * good. The user must refresh (web) or update the app (native).
   */
  schemaSkew: boolean;
  /**
   * True once the server refused the socket's session outright (expired or
   * invalid token). Nothing will sync again until the user signs back in; a
   * reload/relaunch reaches the sign-in screen with the offline queue intact.
   */
  sessionRejected: boolean;
  clientId: string;
  setMode: (mode: AppMode) => Promise<boolean>;
  logStorage: () => void;
  getRound: () => RoundAggregate;
  retryUnconfirmedActions: () => Promise<Error | null>;
  clearUnconfirmedActions: () => Promise<void>;
  reportError: () => void;
  storage: ClientStorageAdapter;
  infoStore: ClientInfoStore;
  disconnect: (cause?: TerminalDisconnectCause) => void;
  connectedUserIds: string[];
  viewerTrack: Track;
  /**
   * Whether the EFFECTIVE viewer is a speculator (has Speculation Grants
   * access) — resolved during the round load so surfaces gated on it (the
   * proposal page's Speculation tab) are correct at first paint. Always false
   * on non-SFC rounds and on offline boots.
   */
  viewerIsSpeculator: boolean;
  dispatchWithResult: (action: StampableAction) => Promise<Error | null>;
  /**
   * Why this client can't edit the round right now (archived, or the socket
   * is gone for good), or null. Derived here, once, from the aggregate and
   * the mode; `dispatch` refuses with the same lock the UI disables on.
   */
  editLock: EditLock | null;
};

const appModeSchema = z.object({
  adminCanEdit: z.boolean(),
  viewAsTrackMember: z.union([trackSchema, z.null()]),
});

export { APP_MODE_STORAGE_PREFIX } from "@client/impersonationRef";
import { APP_MODE_STORAGE_PREFIX } from "@client/impersonationRef";

function getAppModeStorageKey(roundId: string) {
  return `${APP_MODE_STORAGE_PREFIX}${roundId}`;
}

export function getModeFromLocalStorage(roundId: string): AppMode | null {
  if (!rawLocalStorage.isSupported()) {
    return null;
  }

  const stored = rawLocalStorage.getItem(getAppModeStorageKey(roundId));
  const global = getGlobalImpersonation();
  const globalUserId = global?.userId ?? null;

  if (!stored && !globalUserId) {
    return null;
  }

  // Parse round-scoped mode (may fail if localStorage is corrupted)
  let roundMode: {
    adminCanEdit: boolean;
    viewAsTrackMember: Track | null;
  } | null = null;

  if (stored) {
    try {
      const result = appModeSchema.safeParse(JSON.parse(stored));

      if (result.success) {
        roundMode = result.data;
      }
    } catch {
      // ignore malformed round-scoped localStorage
    }
  }

  if (roundMode) {
    return {
      ...roundMode,
      viewAsUserId: globalUserId,
      disconnected: false,
    };
  }

  // Round mode missing or corrupted — still return with global impersonation if active
  if (globalUserId) {
    return {
      adminCanEdit: false,
      viewAsTrackMember: null,
      viewAsUserId: globalUserId,
      disconnected: false,
    };
  }

  return null;
}

function setModeToLocalStorage(roundId: string, mode: AppMode) {
  if (!rawLocalStorage.isSupported()) {
    return;
  }

  try {
    const valid = appModeSchema.parse(mode);
    rawLocalStorage.setItem(
      getAppModeStorageKey(roundId),
      JSON.stringify(valid),
    );
  } catch {
    // ignore invalid mode values
  }
  // Global impersonation is managed by the ImpersonateContext dropdown,
  // not here — the dropdown has access to the full user list for display names.
}

type ServerToClientEvents = {
  message: (msg: MessageToClient, cb: () => void) => void;
  presence: (userIds: string[]) => void;
  connection: (socket: Socket) => void;
};

type ClientToServerEvents = {
  message: (msg: MessageToServer, cb: () => void) => void;
  connection: (socket: Socket) => void;
  // Page-lifecycle report feeding the server's connection alerts. Fire-and-
  // forget (no ack): losing one only degrades alert wording, never state.
  lifecycle: (state: "visible" | "hidden" | "teardown") => void;
  // Terminal-cause report, same contract (volatile, best-effort).
  disconnectCause: (cause: TerminalDisconnectCause) => void;
};

type Socket = IoSocket<ServerToClientEvents, ClientToServerEvents>;

class ClientWrapper {
  // Actions which have been applied to the storage adapter but not yet emitted
  // (We emit them after the next render so the evaluations cache has the latest
  // data)
  private pendingActions: CurrentAction[] = [];

  client: RoundClient;
  userProfiles: UserProfiles;
  underlyingUserIsSfcAdmin: boolean;
  viewerIsSpeculator: boolean;
  connectedUserIds: string[] = [];

  // Subscribers for useSyncExternalStore
  private listeners = new Set<() => void>();
  // Snapshot for useSyncExternalStore - cached to return stable reference
  private snapshot: State | null = null;

  mode: AppMode = {
    adminCanEdit: false,
    viewAsTrackMember: null,
    viewAsUserId: null,
    disconnected: false,
  };

  emitter = new EventEmitter();
  clientMessageHandler!: (msg: MessageToClient) => void;
  socket: Socket;
  disconnectDetails: DisconnectDetails | null = null;
  // Latched when a server message failed schema validation (this build is
  // older than the deployed action schema). The socket is disconnected for
  // good at that point; the UI uses this to say "refresh / update the app".
  schemaSkewDetected = false;
  sessionRejectedDetected = false;
  private namespaceRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private namespaceRetryAttempt = 0;

  // Small ring buffer of the most recent raw frames received from the engine.io
  // transport. socket.io-parser throws a bare Error("invalid payload") on a bad
  // frame without including the offending bytes, so on a "parse error"
  // disconnect we attach these frames to Sentry to recover the actual message.
  private static readonly MAX_RAW_FRAMES = 5;
  private static readonly MAX_RAW_FRAME_CHARS = 2000;
  private recentRawFrames: RawSocketFrame[] = [];
  // The engine instance we've attached the raw-frame listener to. The engine is
  // recreated on every reconnect, so we re-attach when it changes.
  private rawFrameEngine: unknown = null;
  // React Fast Refresh runs effect cleanups while preserving hook refs. When
  // that happens, we temporarily close the socket but want the preserved
  // wrapper to reconnect when the effect runs again.
  private reconnectSocketAfterTeardown = false;
  // Releases this tab's presence in the round's open-tab count (SP-2823 stall
  // diagnostic). Held while the client is live — taken in both connect paths
  // (wiring the client), dropped in both teardown paths (unmount, terminal
  // disconnect). Not tied to the socket's own connect/disconnect events on
  // purpose: a tab mid-reconnect still applies its catch-up when the socket
  // returns, so it stays counted (see roundTabPresence.ts).
  private releaseTabPresence: (() => void) | null = null;

  // When there's a new client, connect it to our socket.
  // userProfiles probably hasn't changed, but there's a chance it has
  // so we might as well update it.
  connect({
    client,
    userProfiles,
    underlyingUserIsSfcAdmin,
    viewerIsSpeculator,
  }: RoundData) {
    this.client = client;
    this.userProfiles = userProfiles;
    this.underlyingUserIsSfcAdmin = underlyingUserIsSfcAdmin;
    this.viewerIsSpeculator = viewerIsSpeculator;
    this.client.actingAsUserId =
      this.mode.viewAsUserId ?? this.client.underlyingUserId;
    impersonationRef.current = this.mode.viewAsUserId ?? null;

    if (!this.mode.disconnected) {
      this.holdTabPresence();
      this.client.connect(
        (msg) => {
          void this.handleClientMessageToServer(msg);
        },
        (handler) => {
          this.clientMessageHandler = (...args) => {
            void handler(...args);
          };
        },
        () => this.emitChange(),
        (action) => this.emitAction(action.payload),
      );

      if (this.reconnectSocketAfterTeardown) {
        this.reconnectSocketAfterTeardown = false;
        this.disconnectDetails = null;
        // Re-attach the listeners removed during teardown — this wrapper
        // instance is being reused (Fast Refresh / Strict Mode) and the socket
        // is reopened without re-running the constructor. The lifecycle
        // reporter must come back too, or the socket keeps advertising
        // `lifecycle: true` while sending nothing and quiet closes read as
        // "lost connection".
        this.attachWindowOnlineListener();
        this.attachLifecycleReporter();
        this.socket.connect();
      }
    }

    // Invalidate snapshot so getSnapshot returns fresh state with new userProfiles
    this.emitChange();
  }

  constructor({
    client,
    userProfiles,
    underlyingUserIsSfcAdmin,
    viewerIsSpeculator,
  }: RoundData) {
    this.client = client;
    this.userProfiles = userProfiles;
    this.underlyingUserIsSfcAdmin = underlyingUserIsSfcAdmin;
    this.viewerIsSpeculator = viewerIsSpeculator;

    const storedMode = getModeFromLocalStorage(client.aggregateId);

    if (storedMode) {
      // Don't restore impersonation for non-admin users (stale localStorage
      // from a previous session with a different account).
      if (
        storedMode.viewAsUserId != null &&
        !userIsAdmin(client.underlyingUserId, {
          round: client.getAggregate(),
          userProfiles,
          effectiveIsSfcAdmin: underlyingUserIsSfcAdmin,
        })
      ) {
        storedMode.viewAsUserId = null;
        storedMode.viewAsTrackMember = null;
      }

      this.client.actingAsUserId =
        storedMode.viewAsUserId ?? this.client.underlyingUserId;
      impersonationRef.current = storedMode.viewAsUserId ?? null;
      this.mode = storedMode;
    }

    this.holdTabPresence();
    this.client.connect(
      (msg) => {
        void this.handleClientMessageToServer(msg);
      },
      (handler) => {
        this.clientMessageHandler = (...args) => {
          void handler(...args);
        };
      },
      () => this.emitChange(),
      (action) => this.emitAction(action.payload),
    );

    const { aggregateId: roundId, id: clientId } = client;
    // On bundled native builds the WebView origin is `capacitor://localhost`,
    // so a relative namespace would never reach the API host — prefix the
    // native base URL. Native clients also have no session cookie, so they
    // pass the bearer token as the Socket.IO `auth` payload (the server
    // middleware accepts it). Web/dev-server builds keep the relative,
    // cookie-authenticated connection unchanged (base "" + no token).
    const socketBaseUrl = getClientBaseUrl();
    const authToken = Capacitor.isNativePlatform() ? loadAuthToken() : null;
    this.socket = io(`${socketBaseUrl}/${roundId}/${clientId}`, {
      // Connect over a real WebSocket first instead of Socket.IO's default
      // HTTP-long-polling-then-upgrade dance. Polling connections count against
      // the browser's ~6-connections-per-host HTTP/1.1 budget, so a user with
      // many tabs of this app open can starve that budget and see connections
      // stall until they reconnect. WebSocket has a far higher per-host ceiling
      // and connects in one step (no upgrade round trip).
      //
      // `tryAllTransports` keeps the polling fallback: if WebSocket can't be
      // established (e.g. a corporate proxy that blocks it), the client falls
      // back to polling rather than failing outright. Without this flag a
      // websocket-first list would simply give up on failure — the flag is what
      // makes preferring WebSocket safe on restrictive networks.
      transports: ["websocket", "polling"],
      tryAllTransports: true,
      retries: 2,
      // The auth payload doubles as the client's self-description for the
      // server's connection alerts. `lifecycle: true` promises the server
      // page-lifecycle reports (the "lifecycle" emits below), which upgrade
      // its end-of-session wording from the soft legacy fallback to
      // idle/closed/lost classification. Native builds add `device` and
      // `lockdown` (boot-time IndexedDB probe — the live global can't be
      // trusted because the storage stand-ins install a fake into it);
      // browsers are identified by their cookie auth and send no token.
      // A function, not an object: Socket.IO calls it on EVERY connection
      // attempt, so each handshake carries the page's CURRENT visibility as
      // `lifecycle` — atomically with the connection itself. A separate
      // post-connect event could be lost if the socket died first, leaving
      // the server's default ("visible") to misread a backgrounded client's
      // death as an alarming "lost connection".
      auth: (cb) =>
        cb({
          lifecycle:
            typeof document !== "undefined" &&
            document.visibilityState === "hidden"
              ? "hidden"
              : "visible",
          // Lets the connection alerts link straight to the PostHog session
          // recording. Best-effort: absent until PostHog finishes its lazy
          // init, so a reconnect (fresh auth evaluation) usually carries it
          // even when the first connect raced the init.
          ...(getPostHogSessionReplay().url
            ? { posthogReplay: getPostHogSessionReplay().url }
            : {}),
          ...(authToken
            ? {
                token: authToken,
                device: isIPadCapacitor() ? "ipad" : "iphone",
                lockdown: indexedDBWasMissingAtBoot,
              }
            : // Browser tab on an iPad: the client must say so, because iPadOS
              // Safari's User-Agent masquerades as desktop Mac Safari — the
              // server cannot tell them apart. isIPad() sees through it via
              // maxTouchPoints.
              isIPad()
              ? { browserDevice: "ipad" }
              : {}),
        }),
    });
    this.attachLifecycleReporter();
    // Capture raw transport frames into the ring buffer. The engine is recreated
    // on every (re)connect, so re-attach whenever the manager opens a new one.
    this.socket.io.on("open", () => this.attachRawFrameListener());
    this.attachRawFrameListener();
    this.socket.on("connect", () => {
      this.namespaceRetryAttempt = 0;
      this.clearNamespaceRetry();
      console.debug("[action-sync] socket connected", {
        roundId,
        clientId,
        socketId: this.socket.id,
      });
      this.client.register();
      this.emitChange();
    });
    this.socket.on(
      "disconnect",
      (
        reason: IoSocket.DisconnectReason,
        description?: DisconnectDescription | undefined,
      ) => {
        this.disconnectDetails = {
          reason,
          description,
          willReconnect: this.socket.active,
          roundId,
          clientId,
          recentRawFrames: this.getRecentRawFrames(),
        };
        console.warn("[action-sync] socket disconnected", {
          roundId,
          clientId,
          socketId: this.socket.id,
          reason,
          description: description ?? undefined,
          willReconnect: this.socket.active,
        });
        this.emitChange();
      },
    );

    this.socket.on("connect_error", (error: Error) => {
      const message = error?.message ?? String(error);
      console.warn("[action-sync] socket connect_error", {
        roundId,
        clientId,
        message,
      });

      if (isSessionRejection(message)) {
        // The server refused the session itself. Latch and surface — the
        // cold-boot path already lands a dead session on the sign-in screen
        // with the offline queue intact, so a reload/relaunch is the way out.
        this.sessionRejectedDetected = true;
        this.clearNamespaceRetry();
        this.emitChange();
        return;
      }

      // Engine-level failures (server unreachable, mid-restart) leave the
      // manager active and retrying on its own — scheduling on top of that
      // would double-connect and burn the namespace budget on noise.
      if (this.socket.active) {
        this.emitChange();
        return;
      }

      // Transient namespace rejection (middleware exception): the manager
      // considers the connection done and will NOT retry, so we do, bounded.
      this.namespaceRetryAttempt += 1;
      const delay = connectErrorRetryDelayMs(this.namespaceRetryAttempt);

      if (delay == null) {
        if (this.disconnectDetails) {
          this.disconnectDetails = {
            ...this.disconnectDetails,
            willReconnect: false,
          };
        }

        this.emitChange();
        return;
      }

      this.clearNamespaceRetry();
      this.namespaceRetryTimer = setTimeout(() => {
        this.namespaceRetryTimer = null;

        // The unmount path disconnects the socket; a live timer must not
        // resurrect it afterwards.
        if (!this.mode.disconnected && !this.socket.connected) {
          this.socket.connect();
        }
      }, delay);
      this.emitChange();
    });

    const processServerMessage = createServerMessageProcessor({
      handleMessage: async (message) => {
        console.debug("[action-sync] socket message received", {
          roundId,
          clientId,
          ...summarizeMessageToClient(message),
        });
        await this.client.handleMessage(message);
        console.debug("[action-sync] socket message processed", {
          roundId,
          clientId,
          ...summarizeMessageToClient(message),
        });
      },
      onSchemaSkew: (error, unparsedMessage) => {
        // This build's schema can't read what the server sent — usually a
        // client older than the deployed action schema (an un-reloaded tab,
        // or the bundled native app). Retrying can never succeed and the
        // server redelivers unacknowledged batches every few seconds, so
        // report once, disconnect for good, and let the UI tell the user to
        // refresh/update.
        const summary = summarizeUnparsedServerMessage(unparsedMessage);
        console.error(
          "[action-sync] message schema skew — this build cannot parse the server's messages; disconnecting",
          { roundId, clientId, ...summary },
        );
        Sentry.withScope((scope) => {
          scope.setLevel("warning");
          scope.setTag("sync_issue", "message_schema_skew");
          scope.setTag("round_id", roundId);
          scope.setTag("client_id", clientId);
          scope.setContext("skewed_message", summary);
          Sentry.captureException(error);
        });
        this.schemaSkewDetected = true;
        this.disconnect("schema-skew");
      },
    });

    this.socket.on("message", async (unparsedMessage) => {
      try {
        await processServerMessage(unparsedMessage);
      } catch (error) {
        // Apply failures on a message that parsed fine — not skew; keep the
        // pre-existing behavior (per-occurrence report and rethrow).
        console.error("Error handling message from server", unparsedMessage);
        console.error(error);
        Sentry.captureException(error);
        throw error;
      }
    });

    this.socket.on("presence", (userIds: string[]) => {
      this.connectedUserIds = userIds;
      this.emitChange();
    });

    this.attachWindowOnlineListener();
  }

  emitAction(action: CurrentAction) {
    // do after the next render. When processing the client queue
    // this.emitChange() may or may not be called, if it is called we want this to
    // always run after the render triggered by this.emitChange(), because subscribers
    // to the action (of which useEvaluatorEvaluationsCache is the only one), won't
    // have current data until the render has run.
    this.pendingActions.push(action);
  }

  drainPendingActions() {
    const actions = this.pendingActions;
    this.pendingActions = [];

    for (const action of actions) {
      this.emitter.emit(action.type, action);
    }
  }

  sendMessageToClient(msg: MessageToClient) {
    this.clientMessageHandler(msg);
  }

  // The socket can connect before the browser reports online, suppressing its
  // Register as well as queued edits. Register again before resending so the
  // server can deliver confirmations even if no further connect event fires.
  // register() also resends the durable queue; repeated actions are deduped.
  private handleWindowOnline = () => {
    this.client.register();
  };

  // Idempotent (re)attach so it survives a teardown→reconnect of the same
  // wrapper (Fast Refresh / Strict Mode), where connect() reopens the socket
  // without re-running the constructor.
  private attachWindowOnlineListener() {
    if (typeof window === "undefined") {
      return;
    }

    window.removeEventListener("online", this.handleWindowOnline);
    window.addEventListener("online", this.handleWindowOnline);
  }

  // Report page-lifecycle transitions to the server over the socket, feeding
  // the connection alerts' end-of-session classification: a socket that dies
  // while the page was hidden means the user had stepped away; one that dies
  // while visible means the connection broke in their face. Browsers also
  // report `teardown` on pagehide (a real unload — tab close or navigation);
  // the native shell deliberately does NOT, because iOS fires pagehide on
  // mere backgrounding, and the app resumes the same page afterwards —
  // teardown must stay a genuinely-terminal, browser-only signal.
  //
  // The current state is (re)announced on every socket "connect": each
  // connection is a fresh socket on the server (which defaults to visible),
  // and a page can connect while already hidden — a background-tab restore
  // or a reconnect after the iPad wakes — which must not read as a
  // died-while-visible "lost connection" later.
  private attachLifecycleReporter() {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return;
    }

    this.detachLifecycleReporter?.();

    const emit = (state: "visible" | "hidden" | "teardown") => {
      // MUST be volatile: with `retries` set on this socket, a plain emit
      // joins the ack-driven retry queue, and an event the server never acks
      // would wedge the queue head and block every subsequent `message`
      // emit — registration and action sync included. Volatile bypasses the
      // queue and drops when disconnected, which is exactly the
      // fire-and-forget contract lifecycle reports want (the handshake
      // carries the initial state; the next transition re-reports).
      if (this.socket.connected) {
        this.socket.volatile.emit("lifecycle", state);
      }
    };

    const emitCurrent = () =>
      emit(document.visibilityState === "hidden" ? "hidden" : "visible");

    const onPageHide = (event: PageTransitionEvent) => {
      // `persisted` means the page is entering the back/forward cache — the
      // instance survives and can resume (iOS Safari backgrounding, desktop
      // back/forward navigation). That is an "idle" story, not a close:
      // emitting teardown here would let every Safari-on-iPad app-switch
      // pierce the grace window as a fake close→reopen pair. Only a
      // non-persisted pagehide is a true unload.
      if (!Capacitor.isNativePlatform() && !event.persisted) {
        emit("teardown");
      }
    };

    document.addEventListener("visibilitychange", emitCurrent);
    window.addEventListener("pagehide", onPageHide);
    this.socket.on("connect", emitCurrent);
    emitCurrent();

    this.detachLifecycleReporter = () => {
      document.removeEventListener("visibilitychange", emitCurrent);
      window.removeEventListener("pagehide", onPageHide);
      this.socket.off("connect", emitCurrent);
      this.detachLifecycleReporter = null;
    };
  }

  private detachLifecycleReporter: (() => void) | null = null;

  // Attach a raw-frame listener to the current engine.io transport. The "data"
  // event fires with the exact string passed to socket.io-parser's decoder, so
  // the most recent frame at disconnect time is the one that failed to parse.
  // Idempotent per engine; the engine is recreated on reconnect, so we re-bind
  // only when it changes.
  private attachRawFrameListener() {
    const engine = this.socket.io.engine;

    if (!engine || engine === this.rawFrameEngine) {
      return;
    }

    this.rawFrameEngine = engine;
    engine.on("data", (data: unknown) => this.recordRawFrame(data));
  }

  private recordRawFrame(data: unknown) {
    let type: string;
    let length: number;
    let head: string;

    if (typeof data === "string") {
      type = "string";
      length = data.length;
      head = data.slice(0, ClientWrapper.MAX_RAW_FRAME_CHARS);
    } else if (data instanceof ArrayBuffer) {
      type = "ArrayBuffer";
      length = data.byteLength;
      head = "<binary frame>";
    } else {
      type = typeof data;
      length = -1;
      head = "<non-string frame>";
    }

    this.recentRawFrames.push({ at: Date.now(), type, length, head });

    if (this.recentRawFrames.length > ClientWrapper.MAX_RAW_FRAMES) {
      this.recentRawFrames.shift();
    }
  }

  private getRecentRawFrames(): RawSocketFrame[] {
    return this.recentRawFrames.slice();
  }

  private cappedResendTimer: ReturnType<typeof setTimeout> | null = null;
  private scheduleCappedEmitResend() {
    if (this.cappedResendTimer != null) {
      return;
    }

    this.cappedResendTimer = setTimeout(() => {
      this.cappedResendTimer = null;

      // re-register (idempotent) AND resend unconfirmed: the capped emit may
      // have been a Register whose ack was lost, in which case resending only
      // PerformActions would leave the client unregistered. register() sends
      // Register then resends the queue, covering both. (Timer is cleared on
      // disconnect, so the socket should still be connected here.)
      try {
        this.client.register();
      } catch {
        // not connected — nothing to do
      }
    }, 10_000);
  }
  private clearCappedEmitResend() {
    if (this.cappedResendTimer != null) {
      clearTimeout(this.cappedResendTimer);
      this.cappedResendTimer = null;
    }
  }

  async handleClientMessageToServer(msg: MessageToServer) {
    const summary = summarizeMessageToServer(msg);
    // Offline gate + bounded retry live in runEmitWithRetry (unit-tested):
    //   - offline ⇒ don't emit; the action is durable in the unconfirmed-action
    //     queue and resends on reconnect via register().
    //   - online ⇒ emit, retrying up to MAX_SOCKET_EMIT_ATTEMPTS on a failed
    //     ack, then give up (reconnect → register() resends). Previously the
    //     failed-emit callback re-invoked itself with no cap — an infinite loop.
    // emitOnce wraps one ack'd socket emit as a promise: resolves the ack error
    // (truthy ⇒ retry, falsy ⇒ success) and rejects only if the emit itself
    // throws, which the catch below surfaces as a PerformActionFailed.
    const emitOnce = (attempt: number) =>
      new Promise<unknown>((resolve, reject) => {
        try {
          // Marked BEFORE the retry goes on the wire, not after the retry loop
          // finishes. The server broadcasts the confirmation before it acks
          // (server/app.ts: broadcastNewActions() then callback()), so a
          // post-loop report always loses the race: the confirmation event has
          // already been emitted, carrying was_resent: false, by the time we
          // learn how many attempts it took.
          if (attempt > 1 && msg.type === "PerformAction") {
            this.client.noteTransportRetried(msg.action.id);
          }

          console.debug("[action-sync] socket message emit", {
            attempt,
            ...summary,
          });
          emitBounded(this.socket, "message", [msg], (err: unknown) => {
            if (err) {
              console.warn("[action-sync] socket message emit failed", {
                attempt,
                ...summary,
                error: err,
              });
            }

            resolve(err ?? null);
          });
        } catch (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
        }
      });

    try {
      const result = await runEmitWithRetry(getIsOnline(), emitOnce);

      // Telemetry belongs HERE, not in Client.performAction: this is the only
      // layer that knows whether the action reached the wire. The client records
      // its send optimistically before this gate, so without this callback an
      // offline transmission is reported as a clean steady-state save.
      //
      // Suppression is safe to report after the fact, unlike a retry: nothing
      // was sent, so no confirmation can be in flight to race it.
      if (msg.type === "PerformAction" && !result.emitted) {
        this.client.noteTransportSuppressed(
          msg.action.id,
          msg.action.payload.type,
        );
      }

      if (!result.emitted) {
        console.debug(
          "[action-sync] offline; not emitting (will resend on reconnect)",
          { ...summary },
        );
      } else if (result.acked) {
        console.debug("[action-sync] socket message acknowledged by server", {
          attempt: result.attempts,
          ...summary,
        });
      } else {
        console.warn(
          "[action-sync] giving up emit after max attempts; scheduling a resend",
          { attempt: result.attempts, ...summary },
        );
        // A reconnect would also resend via register(), but if the socket stays
        // connected (just a lost ack) none fires — so schedule one ourselves.
        this.scheduleCappedEmitResend();
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error("[action-sync] socket message emit threw", {
        ...summary,
        error,
      });

      switch (msg.type) {
        case "PerformAction": {
          this.sendMessageToClient({
            type: "PerformActionFailed",
            actionId: msg.action.id,
            message: (error as Error).message,
          });
          break;
        }

        case "Register":
          toast.error("Error registering " + (error as Error).message);
          break;
        case "AcknowledgeActions":
          console.warn("Error acknowledging actions", error);
          break;
        default:
          assertUnreachable(msg);
      }
    }
  }

  // useSyncExternalStore API: subscribe to changes
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  // useSyncExternalStore API: get current snapshot
  // Must return a cached value - only recompute when emitChange invalidates it
  getSnapshot = (): State => {
    if (this.snapshot === null) {
      this.snapshot = this.computeState();
    }

    return this.snapshot;
  };

  // Notify all subscribers that state has changed
  private emitChange() {
    // Invalidate cached snapshot so getSnapshot computes fresh state
    this.snapshot = null;

    for (const listener of this.listeners) {
      listener();
    }
  }

  setMode = async (mode: AppMode): Promise<boolean> => {
    const unconfirmedActions =
      await this.client.storage.getUnconfirmedActions();

    if (unconfirmedActions.length > 0) {
      toast.error("Cannot set mode while there are unconfirmed actions");
      return false;
    }

    this.client.actingAsUserId =
      mode.viewAsUserId ?? this.client.underlyingUserId;
    impersonationRef.current = mode.viewAsUserId ?? null;
    this.mode = mode;
    setModeToLocalStorage(this.client.aggregateId, mode);

    // Per-round view-as state exists without the global key (View as Track
    // Member) — stamp the owner so the cross-account reconcile can see it.
    if (mode.viewAsUserId != null || mode.viewAsTrackMember != null) {
      stampImpersonationStateOwner(this.client.underlyingUserId);
    }

    // Keep global impersonation in sync. If the dropdown already set the
    // correct userId with a good display name, don't overwrite it — the
    // dropdown has access to the full user list while we only have round profiles.
    const currentGlobal = getGlobalImpersonation();

    if (mode.viewAsUserId) {
      if (currentGlobal?.userId !== mode.viewAsUserId) {
        const profile = this.userProfiles[mode.viewAsUserId];
        setGlobalImpersonation({
          userId: mode.viewAsUserId,
          displayName: profile?.name ?? profile?.email ?? mode.viewAsUserId,
          isSfcAdmin: profile?.isSfcAdmin ?? false,
          email: profile?.email,
          setByUserId: this.client.underlyingUserId,
        });
      }
    } else {
      setGlobalImpersonation(null);
    }

    this.emitChange();
    notifyImpersonationListeners();
    return true;
  };

  getViewerTrack = () => {
    const agg = this.client.getAggregate();

    if (this.mode.viewAsTrackMember) {
      return this.mode.viewAsTrackMember;
    } else if (this.mode.viewAsUserId) {
      return agg.members[this.mode.viewAsUserId]?.track ?? "NO_TRACK";
    } else {
      return agg.members[this.client.underlyingUserId]?.track ?? "NO_TRACK";
    }
  };

  getRound = () => {
    return anonymizeAggregate(
      this.getViewerTrack(),
      this.client.getAggregate(),
      this.mode.viewAsUserId ?? this.client.underlyingUserId,
    );
  };

  logStorage = () => {
    // Available for debugging in devtools via imperativeState.logStorage()
    return this.client.storage;
  };

  retryUnconfirmedActions = () => {
    return this.client.retryUnconfirmedActions();
  };

  clearUnconfirmedActions = async () => {
    await this.client.clearUnconfirmedActions();
    this.emitChange();
  };

  reportError = () => {
    const error = new Error("User clicked report error button");

    if (this.client.storage instanceof SPAStorageAdapterWrapper) {
      void this.client.storage.captureException(error);
    } else {
      Sentry.captureException(error);
    }
  };

  disconnect = (cause?: TerminalDisconnectCause) => {
    // Tell the server WHY before closing (volatile — see the lifecycle
    // reporter for why nothing here may join the ack-driven retry queue).
    if (cause !== undefined && this.socket.connected) {
      this.socket.volatile.emit("disconnectCause", cause);
    }

    // Stubbing `this.client.handleMessage` protects against processing a
    // ConfirmActions that was already in flight when we decided to stop —
    // specifically for the engineIsOutOfDate path, where the local reducer
    // schema may no longer match the server and applying the action could
    // corrupt state. This is safe here because `Client` is now 1:1 with
    // `ClientWrapper` (constructed per wrapper in Round.tsx), so the mutation
    // doesn't leak into a future wrapper.
    this.client.handleMessage = async () => {};

    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.handleWindowOnline);
    }

    this.clearCappedEmitResend();
    this.detachLifecycleReporter?.();
    this.dropTabPresence();
    this.socket.disconnect();
    this.mode.disconnected = true;
    this.emitChange();
  };

  private holdTabPresence() {
    this.releaseTabPresence?.();
    this.releaseTabPresence = holdRoundTabPresence(
      this.client.aggregateId,
      this.client.id,
    );
  }

  private dropTabPresence() {
    this.releaseTabPresence?.();
    this.releaseTabPresence = null;
  }

  private clearNamespaceRetry() {
    if (this.namespaceRetryTimer != null) {
      clearTimeout(this.namespaceRetryTimer);
      this.namespaceRetryTimer = null;
    }
  }

  disconnectForRoundWrapperUnmount = () => {
    this.clearNamespaceRetry();

    if (this.mode.disconnected) {
      return;
    }

    // Used by React effect cleanup, not by the engine-out-of-date guard. This
    // path is recoverable because local HMR may immediately remount effects
    // against the same ClientWrapper instance.
    this.reconnectSocketAfterTeardown = true;

    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.handleWindowOnline);
    }

    this.clearCappedEmitResend();
    this.detachLifecycleReporter?.();
    this.dropTabPresence();
    this.socket.disconnect();
    this.emitChange();
  };

  private computeState(): State {
    const agg = this.client.getAggregate();
    const viewerTrack = this.getViewerTrack();

    return {
      underlyingUserId: this.client.underlyingUserId,
      underlyingUserIsSfcAdmin: this.underlyingUserIsSfcAdmin,
      effectiveIsSfcAdmin:
        this.client.actingAsUserId !== this.client.underlyingUserId
          ? false
          : this.underlyingUserIsSfcAdmin,
      actingAsUserId: this.client.actingAsUserId,
      round: anonymizeAggregate(
        viewerTrack,
        agg,
        this.mode.viewAsUserId ?? this.client.underlyingUserId,
      ),
      confirmedApplicationsOpen: areRoundApplicationsOpen({
        ...this.client.storage.getConfirmedAggregateSync(),
        applicationWindow: undefined,
      }),
      confirmedApplicationWindow:
        this.client.storage.getConfirmedAggregateSync().applicationWindow,
      confirmedApplicationAudience: getRoundApplicationAudience(
        this.client.storage.getConfirmedAggregateSync(),
      ),
      userProfiles: this.userProfiles,
      mode: this.mode,
      subscribe: this.subscribeToAction,
      unsubscribe: this.unsubscribeFromAction,
      socketStatus: this.socket.connected
        ? "connected"
        : this.disconnectDetails,
      schemaSkew: this.schemaSkewDetected,
      sessionRejected: this.sessionRejectedDetected,
      clientId: this.client.id,
      setMode: this.setMode,
      unconfirmedActions: this.client.storage.getUnconfirmedActions(),
      logStorage: this.logStorage,
      getRound: this.getRound,
      retryUnconfirmedActions: this.retryUnconfirmedActions,
      clearUnconfirmedActions: this.clearUnconfirmedActions,
      reportError: this.reportError,
      disconnect: this.disconnect,
      storage: this.client.storage,
      infoStore: this.client.infoStore,
      connectedUserIds: this.connectedUserIds,
      viewerTrack,
      viewerIsSpeculator: this.viewerIsSpeculator,
      dispatchWithResult: this.dispatchWithResult,
      editLock: getEditLock(agg, this.mode),
    };
  }

  subscribeToAction = <T extends ActionType>(
    actionType: T,
    cb: (action: CurrentAction) => void,
  ): void => {
    this.emitter.on(actionType, cb);
  };

  unsubscribeFromAction = <T extends ActionType>(
    actionType: T,
    cb: (eventDetail: CurrentAction) => void,
  ): void => {
    this.emitter.off(actionType, cb);
  };

  async dispatch(action: StampableAction): Promise<Error | null> {
    // A locked round is a state, not a failure: refuse here with the lock's
    // own message, before the reducer runs and before anything reaches
    // Sentry. Restore is the one action that must get through on an
    // archived round, or nobody could ever unlock it.
    const lock = getEditLock(this.getRound(), this.mode);
    const isRestore =
      (action.type === "ROUND_ARCHIVED_CHANGE" &&
        (lock?.reason === "archived" || lock?.reason === "completed")) ||
      (action.type === "ROUND_COMPLETED_CHANGE" &&
        lock?.reason === "completed");

    if (lock && !isRestore) {
      toast.error(lock.message);
      return new EditLockedError(lock);
    }

    const result = await this.client.dispatch(action);

    if (result instanceof Error) {
      captureExceptionWithPriority(result);
      return result;
    }

    return null;
  }

  async dispatchAndIgnore(action: StampableAction): Promise<void> {
    const error = await this.dispatch(action);

    if (!error) {
      return;
    }

    if (error instanceof EditLockedError) {
      // `dispatch` already toasted the lock's message.
      return;
    }

    toast.error("Error dispatching action " + error.message);
  }

  dispatchWithResult = (action: StampableAction): Promise<Error | null> =>
    this.dispatch(action);
}

export type Dispatch = (action: StampableAction) => Promise<void>;

export type StateExternalStore<TState = State> = {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => TState;
};

/**
 * Creates and manages the round's ClientWrapper WITHOUT subscribing to it.
 * The round shell (RoundWrapper) calls this; it must stay render-inert on
 * store emissions or every mounted round screen parent-renders from above,
 * defeating the per-field selector hooks in round-context.ts. Components
 * that need state subscribe through the returned store (or the StateContext
 * built from it) with their own narrow selectors.
 *
 * The third element is drainPendingActions: the caller must invoke it from
 * an effect in a component that re-renders on every store emission, so
 * action events fire after the render they belong to (see emitAction).
 */
export function useEventSourcing(
  roundData: RoundData,
): [Dispatch, StateExternalStore, () => void] {
  // Lazily initialize the ClientWrapper once and keep it stable
  const wrapperRef = useRef<ClientWrapper | null>(null);

  if (wrapperRef.current === null) {
    wrapperRef.current = new ClientWrapper(roundData);
  }

  const wrapper = wrapperRef.current;

  // When roundData changes (e.g., userProfiles updated), reconnect with new data
  // This must be in useEffect to avoid side effects during render (concurrent mode safety)
  useEffect(() => {
    wrapper.connect(roundData);
  }, [wrapper, roundData]);

  // Disconnect socket when RoundWrapper unmounts (e.g. when its key changes).
  // This is separate from the connect effect because that effect's cleanup fires on
  // every roundData change. Use a recoverable teardown path because React Fast
  // Refresh also runs effect cleanups while keeping refs alive.
  useEffect(() => {
    return () => {
      wrapper.disconnectForRoundWrapperUnmount();
    };
  }, [wrapper]);

  // Stable dispatch function - wrapper never changes
  const dispatch = useCallback(
    (action: StampableAction) => wrapper.dispatchAndIgnore(action),
    [wrapper],
  );

  const stateStoreRef = useRef<StateExternalStore | null>(null);

  if (stateStoreRef.current === null) {
    stateStoreRef.current = {
      subscribe: wrapper.subscribe,
      getSnapshot: wrapper.getSnapshot,
    };
  }

  const drainPendingActions = useCallback(
    () => wrapper.drainPendingActions(),
    [wrapper],
  );

  return [dispatch, stateStoreRef.current, drainPendingActions] as const;
}
